/**
 * Member-message workflow steps (TRUS-703) and their merge fields.
 *
 * A step whose start texts or emails the member renders its message through
 * the org's template-variable registry (BE #1220). Before a workflow is saved,
 * each such step's subject and body go through POST /api/template-variables/
 * validate: an unknown or turned-off variable stops the save, since the server
 * would refuse to send that step to anyone.
 *
 * TRUS-719: an `input`-kind variable (reason `needs_input`) does not stop the
 * save when the step supplies a value for it in `config.variable_inputs`; the
 * outbox hands those values to the resolver.
 *
 * Staff notification steps (notify_channel / notify_staff) are NOT member
 * messages and keep their own allow-list (notify/notifySteps.ts).
 */

import { templateVariablesApi, type UnresolvedVariable } from '@/components/messaging/templateVariables/api';

/** Step types the backend sends to the member (services/workflow_member_send.py). */
export const MEMBER_MESSAGE_STEP_TYPES = ['outreach', 'communication', 'communication_sms', 'communication_email'];

export function isMemberMessageStep(stepType: string | null | undefined): boolean {
  return !!stepType && MEMBER_MESSAGE_STEP_TYPES.includes(stepType);
}

/** The step's (subject, body) template — the keys the backend reads, in order. */
export function messageStepTexts(config: Record<string, unknown> | null | undefined): { subject: string | null; body: string } | null {
  if (!config) return null;
  let body = '';
  for (const key of ['message_body', 'message_preview', 'message']) {
    const v = config[key];
    if (typeof v === 'string' && v.trim()) {
      body = v;
      break;
    }
  }
  const subject = typeof config.subject === 'string' ? config.subject : null;
  if (!body && !subject) return null;
  return { subject, body };
}

/** Keys of `config.variable_inputs` with a non-blank value. */
export function suppliedInputKeys(config: Record<string, unknown> | null | undefined): Set<string> {
  const raw = config?.variable_inputs;
  const out = new Set<string>();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if ((typeof v === 'string' && v.trim()) || typeof v === 'number' || typeof v === 'boolean') out.add(k);
  }
  return out;
}

type T = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

interface StepLike {
  name?: string | null;
  step_name?: string | null;
  step_type?: string | null;
  config?: Record<string, unknown> | null;
  step_config?: Record<string, unknown> | null;
}

/**
 * The first member-message step whose template uses a variable the org does
 * not have (or has turned off), as a sentence for a toast — or null.
 * A failed validate call returns null: the send path still refuses an
 * unfillable message, so a flaky check must not block saving the workflow.
 */
export async function firstMessageStepVariableProblem(steps: StepLike[], t: T, stepTypeOf?: (s: StepLike) => string | null | undefined): Promise<string | null> {
  for (const step of steps) {
    const type = stepTypeOf ? stepTypeOf(step) : step.step_type;
    if (!isMemberMessageStep(type)) continue;
    const config = step.config ?? step.step_config ?? null;
    const texts = messageStepTexts(config);
    if (!texts) continue;
    const supplied = suppliedInputKeys(config);
    let errors: UnresolvedVariable[] = [];
    try {
      const res = await templateVariablesApi.validate({ subject: texts.subject, body: texts.body, applies_to: 'messages' });
      errors = (res.errors ?? []).filter((e) => !(e.reason === 'needs_input' && supplied.has(e.key)));
    } catch {
      return null;
    }
    if (errors.length > 0) {
      const first = errors[0];
      return t('templateVariables.workflow.stepProblem', '{{step}}: {{token}} — {{message}}', {
        step: step.name || step.step_name || type || '',
        token: `{{${first.key}}}`,
        message: first.message,
      });
    }
  }
  return null;
}
